import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Brain, Calendar, Clock, Target, Loader2, Sparkles,
  ChevronRight, Check, BookOpen, RotateCcw, Flame,
  GraduationCap, Trash2, History, Plus, AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  high: 'border-red-500/30 bg-red-500/5',
  medium: 'border-amber-500/30 bg-amber-500/5',
  low: 'border-green-500/30 bg-green-500/5',
};

const TYPE_ICONS = {
  review: RotateCcw,
  study: BookOpen,
  practice: Target,
  break: Clock,
  exam_prep: GraduationCap,
};

const TYPE_COLORS = {
  review: 'text-amber-400',
  study: 'text-blue-400',
  practice: 'text-purple-400',
  break: 'text-green-400',
  exam_prep: 'text-red-400',
};

const StudyPlanPage = () => {
  const [plan, setPlan] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [genForm, setGenForm] = useState({
    daily_hours: 3,
    plan_days: 7,
    focus_subjects: [],
    exam_dates: [],
  });
  const [newExam, setNewExam] = useState({ title: '', date: '' });

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/study-plan/current`);
      if (res.data?.has_plan) {
        setPlan(res.data.plan);
        setCompletedTasks(res.data.completed_tasks || []);
      } else {
        setPlan(null);
      }
    } catch (err) {
      console.error('Fetch plan error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/study-plan/generate`, genForm);
      if (res.data?.success) {
        setPlan(res.data.plan);
        setCompletedTasks([]);
        setShowGenerator(false);
        setSelectedDay(0);
        toast.success('Study plan generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleComplete = async (taskId) => {
    try {
      const res = await axios.post(`${API}/study-plan/complete-task`, { task_id: taskId });
      if (res.data?.success) {
        setCompletedTasks(prev => [...prev, taskId]);
        toast.success(`Progress: ${res.data.progress_pct}%`);
      }
    } catch (err) {
      toast.error('Failed to mark task complete');
    }
  };

  const handleDismiss = async () => {
    try {
      await axios.delete(`${API}/study-plan/current`);
      setPlan(null);
      setCompletedTasks([]);
      toast.success('Plan archived');
    } catch (err) {
      toast.error('Failed to dismiss plan');
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/study-plan/history`);
      setHistory(res.data?.plans || []);
      setShowHistory(true);
    } catch (err) {
      console.error('History fetch error:', err);
    }
  };

  const addExamDate = () => {
    if (newExam.title && newExam.date) {
      setGenForm(prev => ({
        ...prev,
        exam_dates: [...prev.exam_dates, { ...newExam }],
      }));
      setNewExam({ title: '', date: '' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="study-plan-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Calculate progress
  const totalTasks = plan?.days?.reduce(
    (acc, day) => acc + (day.tasks?.filter(t => t.type !== 'break').length || 0), 0
  ) || 0;
  const completedCount = completedTasks.length;
  const progressPct = totalTasks ? Math.round((completedCount / totalTasks) * 100) : 0;

  return (
    <div className="f500-page space-y-6" data-testid="study-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-[var(--f500-line)] pb-6">
        <div>
          <p className="f500-eyebrow mb-2">— AI Study Plan</p>
          <h1 className="f500-display text-3xl sm:text-4xl">A schedule that adapts.</h1>
          <p className="f500-body text-base mt-2">AI-generated personalized study schedule based on your subjects, goals, and exam dates.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchHistory}
            className="f500-btn f500-btn-secondary"
            data-testid="plan-history-btn"
          >
            <History className="w-4 h-4" /> History
          </button>
          <button
            onClick={() => setShowGenerator(true)}
            className="f500-btn f500-btn-primary"
            data-testid="generate-plan-btn"
          >
            <Sparkles className="w-4 h-4" /> {plan ? 'New Plan' : 'Generate Plan'}
          </button>
        </div>
      </div>

      {/* Generator Modal */}
      <AnimatePresence>
        {showGenerator && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="plan-generator">
              <h3 className="text-xl font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                Generate Study Plan
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm text-[var(--isp-body)] block mb-1">Daily study hours</label>
                  <input
                    type="number"
                    value={genForm.daily_hours}
                    onChange={e => setGenForm({ ...genForm, daily_hours: Number(e.target.value) })}
                    min={1} max={12} step={0.5}
                    className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm"
                    data-testid="daily-hours-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[var(--isp-body)] block mb-1">Plan duration (days)</label>
                  <input
                    type="number"
                    value={genForm.plan_days}
                    onChange={e => setGenForm({ ...genForm, plan_days: Number(e.target.value) })}
                    min={1} max={30}
                    className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm"
                    data-testid="plan-days-input"
                  />
                </div>
              </div>

              {/* Exam Dates */}
              <div className="mb-4">
                <label className="text-sm text-[var(--isp-body)] block mb-2">Upcoming exams (optional)</label>
                {genForm.exam_dates.map((e, i) => (
                  <div key={`item-${i}`} className="flex items-center gap-2 mb-2 text-sm text-[var(--isp-body)]">
                    <GraduationCap className="w-4 h-4 text-red-400" />
                    <span>{e.title} - {e.date}</span>
                    <button onClick={() => setGenForm({
                      ...genForm,
                      exam_dates: genForm.exam_dates.filter((_, idx) => idx !== i)
                    })} className="text-[var(--isp-muted)] hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    placeholder="Exam name"
                    value={newExam.title}
                    onChange={e => setNewExam({ ...newExam, title: e.target.value })}
                    className="flex-1 p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm"
                    data-testid="exam-title-input"
                  />
                  <input
                    type="date"
                    value={newExam.date}
                    onChange={e => setNewExam({ ...newExam, date: e.target.value })}
                    className="p-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm"
                    data-testid="exam-date-input"
                  />
                  <Button size="sm" variant="outline" onClick={addExamDate} className="rounded-lg border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="add-exam-btn" aria-label="Add exam date">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="rounded-xl gap-2 bg-[#0A0A0A] "
                  data-testid="confirm-generate-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                  {generating ? 'Generating...' : 'Generate Plan'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowGenerator(false)}
                  className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="plan-history">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" /> Plan History
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)} className="text-[var(--isp-body)]">Close</Button>
              </div>
              {history.length === 0 ? (
                <p className="text-[var(--isp-body)] text-center py-6">No previous plans</p>
              ) : (
                <div className="space-y-2">
                  {history.map((h, i) => (
                    <div key={`resource-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
                      <div className={`p-2 rounded-lg ${h.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[var(--isp-glass-inner)] text-[var(--isp-muted)]'}`}>
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--isp-heading)] font-medium truncate">{h.plan_data?.plan_name || 'Study Plan'}</p>
                        <p className="text-xs text-[var(--isp-muted)]">{h.created_at?.slice(0, 10)} | {h.plan_days} days</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[var(--isp-body)]">{h.completed_count}/{h.total_tasks} tasks</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${h.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[var(--isp-glass-inner)] text-[var(--isp-muted)]'}`}>
                          {h.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Plan */}
      {plan ? (
        <div className="space-y-6">
          {/* Plan Summary */}
          <Card className="p-5  bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20" data-testid="plan-summary">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-[var(--isp-heading)]">{plan.plan_name || 'Study Plan'}</h2>
                <p className="text-[var(--isp-body)] text-sm mt-1">{plan.summary || ''}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-400">{progressPct}%</p>
                  <p className="text-xs text-[var(--isp-muted)]">{completedCount}/{totalTasks} tasks</p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-[var(--isp-muted)] hover:text-red-400" data-testid="dismiss-plan-btn" aria-label="Dismiss study plan">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Progress value={progressPct} className="h-2 mt-4 bg-white/10" />
          </Card>

          {/* Day Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {plan.days?.map((day, i) => {
              const dayTasks = day.tasks?.filter(t => t.type !== 'break') || [];
              const dayCompleted = dayTasks.filter(t => completedTasks.includes(t.id)).length;
              const dayDone = dayTasks.length > 0 && dayCompleted === dayTasks.length;
              return (
                <button
                  key={`item-${i}`}
                  onClick={() => setSelectedDay(i)}
                  className={`flex-shrink-0 px-4 py-3 rounded-xl text-sm transition-all ${
                    selectedDay === i
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : dayDone
                      ? 'bg-green-500/10 text-green-400/70 border border-green-500/20'
                      : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)] bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]'
                  }`}
                  data-testid={`day-tab-${i}`}
                >
                  <p className="font-medium">{day.date_label || `Day ${day.day}`}</p>
                  <p className="text-[10px] mt-0.5 opacity-70">{dayCompleted}/{dayTasks.length} done</p>
                </button>
              );
            })}
          </div>

          {/* Tasks for Selected Day */}
          {plan.days?.[selectedDay] && (
            <div className="space-y-3">
              {plan.days[selectedDay].tasks?.map((task, i) => {
                const isCompleted = completedTasks.includes(task.id);
                const Icon = TYPE_ICONS[task.type] || BookOpen;
                const iconColor = TYPE_COLORS[task.type] || 'text-blue-400';

                return (
                  <motion.div
                    key={task.id || i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Card
                      className={`p-4  border transition-all ${
                        isCompleted
                          ? 'bg-green-500/5 border-green-500/20 opacity-60'
                          : task.type === 'break'
                          ? 'bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]'
                          : PRIORITY_STYLES[task.priority] || 'bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]'
                      }`}
                      data-testid={`task-${task.id || i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-xl ${isCompleted ? 'bg-green-500/20' : 'bg-[var(--isp-glass-inner)]'}`}>
                          {isCompleted ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Icon className={`w-4 h-4 ${iconColor}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[var(--isp-muted)] font-mono">{task.time_slot}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              task.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                              task.priority === 'medium' ? 'bg-amber-500/20 text-amber-300' :
                              'bg-green-500/20 text-green-300'
                            }`}>
                              {task.priority}
                            </span>
                            {task.duration_minutes && (
                              <span className="text-[10px] text-[var(--isp-muted)]">{task.duration_minutes}min</span>
                            )}
                          </div>
                          <p className={`text-sm font-medium mt-1 ${isCompleted ? 'text-[var(--isp-muted)] line-through' : 'text-[var(--isp-heading)]'}`}>
                            {task.subject && <span className="text-purple-400 mr-1">[{task.subject}]</span>}
                            {task.activity}
                          </p>
                          {task.description && (
                            <p className="text-xs text-[var(--isp-muted)] mt-0.5">{task.description}</p>
                          )}
                        </div>
                        {task.type !== 'break' && !isCompleted && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleComplete(task.id)}
                            className="text-[var(--isp-muted)] hover:text-green-400 rounded-lg"
                            data-testid={`complete-${task.id || i}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Tips */}
          {plan.tips && plan.tips.length > 0 && (
            <Card className="p-5  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="plan-tips">
              <h3 className="text-sm font-semibold text-[var(--isp-body)] mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-400" /> Study Tips
              </h3>
              <div className="space-y-2">
                {plan.tips.map((tip, i) => (
                  <div key={`tip-${i}`} className="flex items-start gap-2 text-sm text-[var(--isp-body)]">
                    <ChevronRight className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      ) : !showGenerator && (
        /* Empty State */
        <Card className="p-12  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] text-center" data-testid="plan-empty">
          <div className="w-20 h-20 mx-auto  bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-6">
            <Brain className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--isp-heading)] mb-2">No Active Study Plan</h2>
          <p className="text-[var(--isp-body)] mb-6 max-w-md mx-auto">
            Let AI analyze your subjects, exams, and weak areas to create a personalized study schedule
          </p>
          <Button
            onClick={() => setShowGenerator(true)}
            className="rounded-xl gap-2 bg-[#0A0A0A] "
            data-testid="cta-generate-plan"
          >
            <Sparkles className="w-4 h-4" /> Generate Study Plan
          </Button>
        </Card>
      )}
    </div>
  );
};

export default StudyPlanPage;
